import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent
} from "react"
import { updateInvoiceClient, confirmClientIdentifications, type CandidateIdentifier } from "@/services/invoiceService"
import { useToast } from "@/hooks/use-toast"
import { useClients } from "@/hooks"
import type { UseFieldEditorReturn } from "@/hooks/useFieldEditor"
import { EDITABLE_FIELD_ATTR, EDITABLE_FIELD_SELECTOR, FOCUS_TARGET_ATTR, FOCUS_TARGET_SELECTOR } from "../focusConstants"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { FilterableSelect } from "@/components/ui/filterable-select"
import { AssignmentCard } from "./AssignmentCard"
import { useInvoiceFieldCategories } from "../hooks/useInvoiceFieldCategories"
import { useInvoiceType } from "../hooks/useInvoiceType"
import type { ExtractedField, Invoice } from "../types"
import { OriginalValueIndicator } from "./OriginalValueIndicator"
import { formatFieldDisplayValue, getEditableValue, isDateField } from "../utils/invoiceFieldHelpers"
import { DateFieldEditor } from "@/components/ui/date-picker"
import { toISODateString } from "@/lib/dateUtils"
import { CountrySelectField } from "./InvoiceFieldsPanel/CountrySelectField"
import { Lock } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface ClientAssignmentCardProps {
  invoice: Invoice
  onInvoiceRefresh?: () => void
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  fieldEditor: UseFieldEditorReturn
  readOnly?: boolean
}

export const ClientAssignmentCard = ({
  invoice,
  onInvoiceRefresh,
  onFieldUpdate,
  fieldEditor,
  readOnly
}: ClientAssignmentCardProps): JSX.Element => {
  const { clients, isLoading: clientsLoading } = useClients()
  const { toast } = useToast()
  const categorizedData = useInvoiceFieldCategories(invoice)
  const vendorFields = categorizedData.vendor
  const customerFields = categorizedData.customer

  const [selectedClientId, setSelectedClientId] = useState<string>(() => invoice.client?.id ?? "none")
  const [isUpdatingClient, setIsUpdatingClient] = useState(false)
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false)
  const clientDropdownNextFocusRef = useRef<HTMLElement | null>(null)
  const clientSelectTriggerRef = useRef<HTMLButtonElement | null>(null)
  const clientDropdownPrimedRef = useRef(false)

  // Client identification confirmation modal state
  const [identCandidates, setIdentCandidates] = useState<CandidateIdentifier[] | null>(null)
  const [identSelected, setIdentSelected] = useState<Set<number>>(new Set())
  const [identClientId, setIdentClientId] = useState<string | null>(null)
  const [identClientName, setIdentClientName] = useState<string | null>(null)
  const [identSaving, setIdentSaving] = useState(false)

  useEffect(() => {
    setSelectedClientId(invoice.client?.id ?? "none")
  }, [invoice.client?.id])

  useEffect(() => {
    clientDropdownPrimedRef.current = false
  }, [invoice.id])

  const queueFocusToNextFocusableElement = useCallback(
    (currentElement?: HTMLElement | null) => {
      if (typeof document === "undefined") return

      const focusTargets = Array.from(document.querySelectorAll<HTMLElement>(FOCUS_TARGET_SELECTOR))

      if (currentElement) {
        const currentIndex = focusTargets.findIndex((target) => target === currentElement)
        if (currentIndex >= 0) {
          const nextTarget = focusTargets[currentIndex + 1]
          if (nextTarget && nextTarget !== currentElement) {
            clientDropdownNextFocusRef.current = nextTarget
            return
          }
        }
      }

      const firstEditable = document.querySelector<HTMLElement>(EDITABLE_FIELD_SELECTOR)
      if (firstEditable && firstEditable !== currentElement) {
        clientDropdownNextFocusRef.current = firstEditable
        return
      }

      const fallbackTarget = focusTargets.find((target) => target !== currentElement) ?? null
      clientDropdownNextFocusRef.current = fallbackTarget
    },
    []
  )

  useEffect(() => {
    const handleInitialTabFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || event.shiftKey) return
      if (clientDropdownPrimedRef.current) return

      const active = document.activeElement
      if (active && active !== document.body) return

      event.preventDefault()
      clientDropdownPrimedRef.current = true
      const trigger = clientSelectTriggerRef.current
      if (trigger) {
        trigger.focus()
        setIsClientDropdownOpen(true)
      }
    }

    document.addEventListener("keydown", handleInitialTabFocus, true)
    return () => {
      document.removeEventListener("keydown", handleInitialTabFocus, true)
    }
  }, [])

  const detectedClient = invoice.client ?? null
  const selectedClient =
    selectedClientId === "none"
      ? null
      : clients.find((client) => client.id === selectedClientId) ??
      (selectedClientId === detectedClient?.id ? detectedClient : null)

  const { isIncome } = useInvoiceType({
    client: selectedClient,
    vendorFields
  })

  const targetFields = isIncome ? vendorFields : customerFields

  const invoiceClientId = invoice.client?.id ?? null
  const invoiceClientInAvailableList = invoiceClientId ? clients.some((client) => client.id === invoiceClientId) : false

  const resolveClientName = useCallback(
    (clientId: string | null) => {
      if (!clientId || clientId === "none") return null
      const availableClient = clients.find((clientOption) => clientOption.id === clientId)
      if (availableClient) return availableClient.name
      if (detectedClient && detectedClient.id === clientId) return detectedClient.name
      return null
    },
    [clients, detectedClient]
  )

  const clientOptions = useMemo(() => {
    const options = [...clients]
    if (detectedClient && !invoiceClientInAvailableList) {
      options.unshift(detectedClient)
    }

    const uniqueById = new Map<string, (typeof clients)[number]>()
    options.forEach((clientOption) => {
      uniqueById.set(clientOption.id, clientOption)
    })

    return Array.from(uniqueById.values())
  }, [clients, detectedClient, invoiceClientInAvailableList])

  // Let FilterableSelect handle capping and search; keep full list so the selected client always stays visible.
  const filteredClients = useMemo(() => clientOptions, [clientOptions])

  const editableFieldAttr = useMemo(
    () => ({
      [EDITABLE_FIELD_ATTR]: "true"
    }),
    []
  )
  const fieldFocusTargetAttr = useMemo(
    () => ({
      [FOCUS_TARGET_ATTR]: "field-input"
    }),
    []
  )

  const formatDisplayValue = useCallback(
    (field: ExtractedField) => formatFieldDisplayValue(field),
    []
  )

  const handleDatePickerSelect = useCallback(
    (fieldId: string, isoValue: string) => {
      onFieldUpdate?.(fieldId, isoValue)
    },
    [onFieldUpdate]
  )

  const handleFieldSave = useCallback(() => {
    fieldEditor.saveField(onFieldUpdate)
  }, [fieldEditor, onFieldUpdate])

  const handleFieldActivation = useCallback(
    (field: ExtractedField) => {
      if (fieldEditor.isEditing(field.id)) {
        return
      }

      fieldEditor.startEditing(field.id, getEditableValue(field))
    },
    [fieldEditor]
  )

  const handleFieldValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      fieldEditor.updateValue(event.target.value)
    },
    [fieldEditor]
  )

  const handleFieldBlur = useCallback(
    (_event: FocusEvent<HTMLInputElement>, fieldId: string) => {
      if (!fieldEditor.isEditing(fieldId)) {
        return
      }

      handleFieldSave()
    },
    [fieldEditor, handleFieldSave]
  )

  const handleFieldInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      fieldEditor.handleKeyDown(event, onFieldUpdate)
    },
    [fieldEditor, onFieldUpdate]
  )

  // Fields that are locked (read-only) when a client is selected
  const LOCKED_CLIENT_FIELDS = new Set([
    "vendor_name", "vendor_address", "vendor_tax_id", "vendor_country",
    "customer_name", "customer_address", "customer_tax_id", "customer_country"
  ])

  const isFieldLockedByClient = useCallback(
    (fieldName: string) => {
      return readOnly || (!!invoice.client && LOCKED_CLIENT_FIELDS.has(fieldName.toLowerCase()))
    },
    [invoice.client, readOnly]
  )

  const renderCustomerFieldValue = useCallback(
    (targetField: ExtractedField) => {
      const placeholderElement = <span className="text-gray-400 italic">No value</span>
      const fieldLocked = isFieldLockedByClient(targetField.field_name)

      // When field is locked by client selection, render as read-only text
      if (fieldLocked) {
        const hasValue =
          targetField.field_value !== undefined &&
          targetField.field_value !== null &&
          targetField.field_value !== ""
        const displayValue = formatDisplayValue(targetField)

        return (
          <div className="flex items-center gap-1 w-full min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Lock className="h-3 w-3 flex-shrink-0 text-neutral-400" />
              </TooltipTrigger>
              <TooltipContent>{readOnly ? "Locked — invoice is exported" : "Synced from Mirage"}</TooltipContent>
            </Tooltip>
            <span
              className="min-w-0 max-w-full text-left text-sm font-semibold text-neutral-950 leading-5 break-words whitespace-normal rounded px-1 py-1"
            >
              {hasValue ? displayValue : <span className="text-gray-400 italic">Not Available</span>}
            </span>
          </div>
        )
      }

      if (targetField.field_name.toLowerCase().endsWith("_country")) {
        return (
          <CountrySelectField
            field={targetField}
            editableFieldAttr={editableFieldAttr}
            focusTargetAttr={fieldFocusTargetAttr}
            onSelectChange={(field, value) => {
              onFieldUpdate?.(field.id, value)
            }}
            disabled={!onFieldUpdate}
          />
        )
      }

      const isEditingField = fieldEditor.isEditing(targetField.id)

      if (isEditingField) {
        if (isDateField(targetField)) {
          return (
            <div className="flex min-w-0 items-center w-full">
              <DateFieldEditor
                value={fieldEditor.editValue}
                onChange={handleFieldValueChange}
                onBlur={(event) => handleFieldBlur(event, targetField.id)}
                onKeyDown={handleFieldInputKeyDown}
                onDateSelect={(day) => {
                  const iso = toISODateString(day)
                  if (iso) handleDatePickerSelect(targetField.id, iso)
                  fieldEditor.cancelEditing()
                }}
                inputClassName="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                focusTargetAttr={fieldFocusTargetAttr}
                autoFocus
              />
            </div>
          )
        }

        return (
          <div className="flex min-w-0 items-center w-full">
            <input
              type="text"
              value={fieldEditor.editValue}
              onChange={handleFieldValueChange}
              onBlur={(event) => handleFieldBlur(event, targetField.id)}
              onKeyDown={handleFieldInputKeyDown}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              {...fieldFocusTargetAttr}
              autoFocus
            />
          </div>
        )
      }

      const hasValue =
        targetField.field_value !== undefined &&
        targetField.field_value !== null &&
        targetField.field_value !== ""

      const displayValue = formatDisplayValue(targetField)

      return (
        <button
          type="button"
          {...editableFieldAttr}
          {...fieldFocusTargetAttr}
          className="w-full min-w-0 max-w-full text-left text-sm font-semibold text-neutral-950 leading-5 break-words whitespace-normal cursor-pointer hover:bg-gray-50 rounded px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          onFocus={() => handleFieldActivation(targetField)}
          onClick={(event) => {
            event.stopPropagation()
            handleFieldActivation(targetField)
          }}
        >
          {hasValue ? displayValue : placeholderElement}
        </button>
      )
    },
    [
      editableFieldAttr,
      fieldEditor,
      fieldFocusTargetAttr,
      formatDisplayValue,
      handleFieldActivation,
      handleFieldBlur,
      handleFieldInputKeyDown,
      handleFieldValueChange,
      handleDatePickerSelect,
      isFieldLockedByClient,
      onFieldUpdate
    ]
  )

  const renderCustomerField = useCallback(
    (field: ExtractedField) => {
      const isDropdown = field.field_name.toLowerCase().endsWith("_country")
      return (
      <div
        key={field.id}
        className={`items-center justify-end px-0 py-1 self-stretch w-full flex-[0_0_auto] 2xl:flex-1 flex ${isDropdown ? "" : "border-b border-neutral-200"} ${fieldEditor.isEditing(field.id) ? "bg-blue-50" : ""
          }`}
      >
        <div className="flex flex-col items-start flex-1 max-w-full min-w-0">
          <div className="flex items-start w-full">
            <div className="flex flex-1 min-w-0 items-center 2xl:flex-col 2xl:items-start">
              <span className="order-2 h-5 truncate text-sm font-normal text-neutral-500 leading-5 whitespace-nowrap ml-4 capitalize 2xl:order-1 2xl:ml-0 2xl:w-full 2xl:whitespace-normal 2xl:overflow-visible 2xl:text-xs">
                {field.field_name.replace(/_/g, " ")}
              </span>
              <div className="order-1 flex-1 min-w-0 2xl:order-2 2xl:w-full flex items-center gap-1">
                {renderCustomerFieldValue(field)}
                <OriginalValueIndicator field={field} />
              </div>
            </div>
          </div>
        </div>
      </div>
    )},
    [fieldEditor, renderCustomerFieldValue]
  )

  const handleIdentConfirm = useCallback(async () => {
    if (!identCandidates || !identClientId || identSelected.size === 0) return
    setIdentSaving(true)
    try {
      const items = Array.from(identSelected).map((idx) => {
        const c = identCandidates[idx]
        return {
          identifier_type: c.identifier_type,
          identifier_value: c.identifier_value,
          existing_identification_id: c.existing_identification?.id ?? null,
        }
      })
      await confirmClientIdentifications(identClientId, items, invoice.id)
      toast({
        title: "Identifiers saved",
        description: `${items.length} identifier${items.length > 1 ? "s" : ""} remembered for future matching.`,
        variant: "success",
        duration: 2000,
      })
    } catch {
      toast({
        title: "Failed to save identifiers",
        variant: "destructive",
        duration: 2000,
      })
    } finally {
      setIdentSaving(false)
      setIdentCandidates(null)
      onInvoiceRefresh?.()
    }
  }, [identCandidates, identClientId, identSelected, invoice.id, toast, onInvoiceRefresh])

  const toggleIdentCandidate = useCallback((idx: number) => {
    setIdentSelected((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }, [])

  return (
    <>
    <AssignmentCard title={selectedClient?.mssql_client_id ? `Client (Mirage: ${selectedClient.mssql_client_id})` : "Client (Mirage)"}>
      <FilterableSelect
        value={selectedClient ? selectedClient.id : selectedClientId}
        options={filteredClients.map((clientOption) => ({
          value: clientOption.id,
          label: clientOption.name,
          details: clientOption.vat_code
        }))}
        onChange={async (value: string) => {
          if (!invoice?.id) return
          if (isUpdatingClient) return

          const previousClientId = selectedClientId
          const nextClientId = value === "none" ? "none" : value
          const targetClientId = nextClientId === "none" ? null : nextClientId
          const currentClientId = invoice.client?.id ?? "none"

          queueFocusToNextFocusableElement(clientSelectTriggerRef.current)
          setIsClientDropdownOpen(false)

          setSelectedClientId(nextClientId)

          if (currentClientId === nextClientId) {
            const infoMessage = targetClientId ? `${invoice.client?.name} is already linked.` : "Invoice already has no client associated."
            toast({
              title: "No changes",
              description: infoMessage,
              duration: 1000
            })
            return
          }

          try {
            setIsUpdatingClient(true)
            const result = await updateInvoiceClient(invoice.id, targetClientId)

            const resolvedName = resolveClientName(targetClientId)
            const successDescription = targetClientId
              ? resolvedName
                ? `${resolvedName} assigned.`
                : "Client association saved."
              : "Client association cleared."

            toast({
              title: "Client updated",
              description: successDescription,
              variant: "success",
              duration: 1000
            })

            // Show identification confirmation modal if backend returned candidates.
            // Defer onInvoiceRefresh when showing the modal — the refresh unmounts
            // this component (parent shows a loading spinner) which destroys modal state.
            if (result.candidate_identifiers?.length && targetClientId) {
              setIdentCandidates(result.candidate_identifiers)
              setIdentSelected(new Set())
              setIdentClientId(targetClientId)
              setIdentClientName(resolvedName)
            } else {
              onInvoiceRefresh?.()
            }
          } catch (error) {
            setSelectedClientId(previousClientId)
            const errorMessage = error instanceof Error ? error.message : "Failed to update invoice client."
            toast({
              title: "Update failed",
              description: errorMessage,
              variant: "destructive",
              duration: 1000
            })
          } finally {
            setIsUpdatingClient(false)
            setIsClientDropdownOpen(false)
          }
        }}
        open={isClientDropdownOpen}
        onOpenChange={setIsClientDropdownOpen}
        disabled={isUpdatingClient || readOnly}
        loading={clientsLoading}
        placeholder={clientsLoading ? "Loading clients..." : "Select a client"}
        searchPlaceholder="Search clients..."
        clearOptionLabel="No client"
        showClearOption
        maxVisible={30}
      />

      <div className="flex-1 flex flex-col">
        {targetFields.length === 0 ? (
          <div className="px-2 py-4 text-sm text-gray-400 italic text-center">
            No {isIncome ? "vendor" : "customer"} data available
          </div>
        ) : (
          <div className="w-full mt-2 flex-1 flex flex-col">
            {targetFields.map(renderCustomerField)}
          </div>
        )}
      </div>
    </AssignmentCard>

    {/* Client identification confirmation modal */}
    <Dialog open={!!identCandidates} onOpenChange={(open) => { if (!open) { setIdentCandidates(null); onInvoiceRefresh?.() } }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Remember identifiers?</DialogTitle>
          <DialogDescription>
            This invoice couldn't be auto-matched to <span className="font-semibold">{identClientName}</span>.
            Select identifiers to remember for future matching.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {identCandidates?.map((c, idx) => (
            <label
              key={idx}
              className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                checked={identSelected.has(idx)}
                onCheckedChange={() => toggleIdentCandidate(idx)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{c.identifier_type}</Badge>
                  <span className="text-sm font-medium truncate">{c.identifier_value}</span>
                </div>
                <div className="text-xs text-muted-foreground">{c.label}</div>
                {c.existing_identification && (
                  <div className="text-xs text-amber-600">
                    Currently mapped to <span className="font-medium">{c.existing_identification.client_name ?? "unknown"}</span> — will be updated
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setIdentCandidates(null); onInvoiceRefresh?.() }}>Skip</Button>
          <Button
            onClick={handleIdentConfirm}
            disabled={identSelected.size === 0 || identSaving}
          >
            {identSaving ? "Saving..." : `Save ${identSelected.size > 0 ? `(${identSelected.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
